// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback } from 'react';
import { Stack, Text, Title } from '@mantine/core';

import { observabilityApi } from '../../api/observability';
import type { LogFilter } from '../../api/observability';
import { LogViewer } from './LogViewer';

/**
 * What this device has done, and what it said while doing it.
 *
 * # WHY THIS EXISTS
 *
 * On 0.1.3 a port change failed with `command "1_configure": context deadline
 * exceeded`, and that string was the entire evidence available. Finding the
 * cause — the CLI changes its prompt to `GWN7806P(config)#` in configuration
 * mode — required an operator to open a terminal, SSH to the switch by hand,
 * and read it themselves.
 *
 * This is that terminal, inside the product, with the credentials removed.
 */
export function DeviceLogs({ deviceId }: { deviceId: string }) {
  const load = useCallback(
    (filter: LogFilter) => observabilityApi.deviceLogs(deviceId, filter),
    [deviceId],
  );

  const download = useCallback(() => observabilityApi.downloadDeviceLogs(deviceId), [deviceId]);

  return (
    <Stack gap="sm">
      <Title order={4}>Activity</Title>
      <Text size="xs" c="dimmed">
        Everything Meshvara sent to this device and everything it sent back. Credential
        values are never recorded; the device&rsquo;s own prompts are, because they are
        what show an authentication was demanded.
      </Text>
      <LogViewer
        load={load}
        download={download}
        emptyMessage="Nothing has been recorded for this device yet. Run an operation and it will appear here."
      />
    </Stack>
  );
}
